import { Fragment, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import { Check, ArrowRight, ShieldCheck, FileSearch, Radar, Sparkles } from 'lucide-react';
import { useStore } from '../lib/store';
import { Reveal, MaskedLine, SectionHead, StatusChip, EASE } from '../components/Bits';

const MARQUEE = [
  'PLAN — Susun Anggaran & Target',
  'CHECK — Ledger Bukti & Dokumen',
  'PAY — Sebelum Transfer Pre-Flight Check',
  'READY — Rayakan Dengan Tenang',
];

const CHAPTERS = [
  {
    n: '01',
    label: 'Mulai dengan Jelas',
    title: 'Pilih bukan hanya karena terlihat meyakinkan.',
    body: 'Sebelum menyepakati vendor, lihat kembali hal-hal penting yang benar-benar sudah kamu cek — identitas, kontak, dan jejak acara nyata.',
    takeaway: 'Yang terlihat bagus belum tentu sudah cukup jelas.',
  },
  {
    n: '02',
    label: 'Tinjau Bukti',
    title: 'Bukti lebih menenangkan daripada janji.',
    body: 'Catat portfolio, referensi, dan detail kontrak yang sudah kamu periksa sendiri, lengkap dengan tanggal pengecekannya.',
    takeaway: 'Dicek oleh Anda — bukan sekadar diasumsikan.',
  },
  {
    n: '03',
    label: 'Sebelum Transfer',
    title: 'Berhenti sejenak sebelum dana keluar.',
    body: 'Sebelum setiap pembayaran besar, pastikan kontrak, penerima, dan nominal sudah klir. Satu jeda kecil, banyak kecemasan berkurang.',
    takeaway: 'Jeda dua menit lebih murah daripada penyesalan.',
  },
  {
    n: '04',
    label: 'Bereskan Perlahan',
    title: 'Kerjakan yang paling dekat dulu.',
    body: 'Persiapan tidak harus selesai sekaligus. SebelumSah membantumu melihat langkah berikutnya yang paling penting, tanpa dibuat panik.',
    takeaway: 'Satu hal rapi hari ini, lebih tenang besok.',
  },
  {
    n: '05',
    label: 'Menuju Hari H',
    title: 'Karena menuju hari bahagia seharusnya membawa lega, bukan menyisakan tanda tanya.',
    body: 'SebelumSah tidak menjanjikan semuanya akan sempurna. Tapi kamu bisa melangkah dengan lebih banyak hal yang sudah jelas.',
    takeaway: 'Cek dulu. Bereskan yang penting. Lalu rayakan.',
  },
];

const EYEBROW = 'font-sans font-semibold text-[13px] sm:text-sm uppercase tracking-[0.24em] text-terra';

function ChapterArt({ i }) {
  const terra = '#B85338';
  const gold = '#D4AF37';
  const arts = [
    <g key="a0" stroke={terra} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <rect x="38" y="26" width="74" height="98" rx="9" />
      <path d="M52 50h46M52 66h46M52 82h30" />
      <path d="M52 104l8 8 17-19" stroke={gold} strokeWidth="2" />
    </g>,
    <g key="a1" stroke={terra} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <rect x="38" y="26" width="74" height="98" rx="9" />
      <path d="M52 50h46M52 66h34" />
      <path d="M50 100q9-13 18 0t18 0t16-4" />
      <circle cx="102" cy="108" r="9" stroke={gold} strokeWidth="2" />
    </g>,
    <g key="a2" stroke={terra} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <circle cx="75" cy="75" r="36" />
      <circle cx="75" cy="75" r="23" stroke={gold} strokeWidth="2" />
      <circle cx="75" cy="75" r="3" fill={terra} stroke="none" />
    </g>,
    <g key="a3" stroke={terra} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <rect x="35" y="36" width="80" height="78" rx="10" />
      <path d="M35 56h80" />
      <path d="M55 28v14M95 28v14" />
      <circle cx="58" cy="76" r="3" fill={gold} stroke="none" />
      <circle cx="75" cy="76" r="3" fill={terra} stroke="none" opacity="0.6" />
      <circle cx="92" cy="76" r="3" fill={terra} stroke="none" opacity="0.6" />
      <circle cx="58" cy="93" r="3" fill={terra} stroke="none" opacity="0.6" />
    </g>,
    <g key="a4" stroke={terra} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <circle cx="62" cy="80" r="24" />
      <circle cx="88" cy="80" r="24" stroke={gold} strokeWidth="2" />
      <path d="M75 40l7 9-7 9-7-9z" stroke={gold} strokeWidth="2" />
    </g>,
  ];
  return (
    <svg width="150" height="150" viewBox="0 0 150 150" className="opacity-80" aria-hidden>
      {arts[i]}
    </svg>
  );
}

function PauseMoment() {
  return (
    <Reveal>
      <div className="relative rounded-[28px] bg-[#F6EBE5]/80 border border-line px-6 py-14 sm:py-16 text-center max-w-3xl mx-auto">
        <p className={EYEBROW}>Sebelum Transfer</p>
        <p className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-ink leading-snug max-w-xl mx-auto">
          “Bukan soal seberapa cepat membayar. Tapi seberapa tenang kamu sebelum uang berpindah.”
        </p>
      </div>
    </Reveal>
  );
}

function ChapterRow({ c, i }) {
  const flip = i % 2 === 1;
  const last = i === CHAPTERS.length - 1;

  if (last) {
    return (
      <Reveal>
        <div className="relative rounded-[28px] blush-cta border border-line px-6 py-12 sm:px-12 text-center max-w-3xl mx-auto">
          <span className="font-serif leading-none text-[56px] sm:text-7xl text-terra/40 select-none">{c.n}</span>
          <p className={`${EYEBROW} mt-3`}>{c.label}</p>
          <h3 className="font-serif text-2xl sm:text-4xl font-medium tracking-tight text-ink mt-4 leading-snug">{c.title}</h3>
          <p className="mt-4 text-base sm:text-[17px] leading-[1.65] text-[#5F5A56] max-w-xl mx-auto">{c.body}</p>
          <p className="mt-6 font-serif italic text-lg sm:text-xl text-terra">{c.takeaway}</p>
          <div className="mt-7 flex justify-center"><ChapterArt i={i} /></div>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <div className="relative grid lg:grid-cols-2 gap-6 lg:gap-20 items-center">
        <span className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-gold/70 ring-4 ring-bone z-10" />
        <div className={flip ? 'lg:order-2' : ''}>
          <div className="flex items-end gap-4">
            <span className="font-serif leading-[0.85] text-[56px] sm:text-[72px] lg:text-[96px] text-terra/35 select-none">{c.n}</span>
            <p className={`${EYEBROW} pb-1.5`}>{c.label}</p>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink mt-3 leading-snug">{c.title}</h3>
          <p className="mt-4 text-base sm:text-[17px] leading-[1.65] text-[#5F5A56] max-w-lg">{c.body}</p>
          <p className="mt-5 font-serif italic text-base sm:text-lg text-ink/85 border-l-2 border-gold/70 pl-4 max-w-md leading-relaxed">{c.takeaway}</p>
        </div>
        <div className={`hidden lg:flex items-center justify-center ${flip ? 'lg:order-1' : ''}`}>
          <ChapterArt i={i} />
        </div>
      </div>
    </Reveal>
  );
}

function HeroShowcase() {
  const chipSage = 'inline-flex items-center rounded-full bg-[#EBF2EC] border border-[#CBDCCE] text-sage text-[8px] font-semibold px-2 py-0.5 tnum';
  const chipAmber = 'inline-flex items-center rounded-full bg-[#FBF1DF] border border-[#EEDCB9] text-amberw text-[8px] font-semibold px-2 py-0.5 tnum';
  return (
    <div className="relative h-[540px]">
      {/* back layer — Hari Ini mini */}
      <motion.div
        animate={{ y: [0, 7, 0] }}
        transition={{ repeat: Infinity, duration: 7, ease: 'easeInOut' }}
        className="absolute left-0 top-20 w-[265px] -rotate-[4deg] opacity-60 blur-[1.5px]"
      >
        <div className="rounded-[28px] bg-white border border-line p-4 shadow-[0_30px_60px_-28px_rgba(19,24,34,0.25)]">
          <div className="rounded-2xl bg-ink text-ivory p-3.5">
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-gold">Yang Perlu Dibereskan</p>
            <p className="mt-1.5 font-serif text-[13px] font-medium leading-snug">Konfirmasi kontrak WO sebelum DP berikutnya</p>
            <p className="mt-1 font-mono tnum text-[10px] text-ivory/60">Rp15.000.000 · 28 Agustus</p>
          </div>
          <div className="mt-2.5 flex items-center justify-between px-1">
            <span className="text-[9px] text-cashmere">Pembayaran Terdekat</span>
            <span className={chipAmber}>2 perlu dicek</span>
          </div>
        </div>
      </motion.div>

      {/* front phone — Payment Guard */}
      <motion.div
        animate={{ y: [0, -9, 0] }}
        transition={{ repeat: Infinity, duration: 5.5, ease: 'easeInOut' }}
        className="absolute right-0 top-0 w-[290px] xl:w-[305px] rotate-[1.5deg] rounded-[40px] bg-white border border-ink/10 p-2 shadow-[0_60px_110px_-35px_rgba(19,24,34,0.4)]"
      >
        <div className="rounded-[32px] bg-ivory border border-line overflow-hidden">
          <div className="flex justify-center pt-2.5 pb-1.5"><span className="w-14 h-1.5 rounded-full bg-ink/10" /></div>
          <div className="px-3.5 pb-4">
            <div className="rounded-2xl bg-ink text-ivory p-4 relative overflow-hidden">
              <div className="kawung-light absolute inset-0 opacity-[0.03] pointer-events-none" />
              <div className="relative">
                <p className="font-mono text-[8px] uppercase tracking-[0.22em] text-terra">Sebelum Transfer</p>
                <p className="font-serif italic text-[11px] text-ivory/85 mt-1">Cek dulu, sebelum bayar.</p>
                <p className="mt-2 font-mono tnum text-[22px] font-semibold tracking-tight">Rp15.000.000</p>
                <p className="text-[10px] text-ivory/60 mt-0.5">DP Tahap 2 · 28 Agustus</p>
                <div className="mt-2.5 flex gap-1.5">
                  <span className="rounded-full bg-[#EBF2EC]/15 border border-[#CBDCCE]/40 text-[#cfe5d3] text-[8px] font-semibold px-2 py-0.5 tnum">9 sudah dicek</span>
                  <span className="rounded-full bg-[#FBF1DF]/15 border border-[#EEDCB9]/40 text-[#F0D9B5] text-[8px] font-semibold px-2 py-0.5 tnum">2 perlu dicek</span>
                </div>
              </div>
            </div>
            <div className="mt-2.5 space-y-1.5">
              {[
                { t: 'Kontrak tersedia', on: true },
                { t: 'Nominal sesuai kesepakatan', on: true },
                { t: 'Ketentuan pembatalan ditinjau', on: false },
              ].map((r) => (
                <div key={r.t} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${r.on ? 'border-[#CBDCCE] bg-[#EBF2EC]/50' : 'border-line bg-white'}`}>
                  <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border ${r.on ? 'bg-sage border-sage' : 'border-ink/25 bg-white'}`}>
                    {r.on && <Check size={9} strokeWidth={4} className="text-white" />}
                  </span>
                  <span className="text-[10px] font-medium text-pewter">{r.t}</span>
                </div>
              ))}
            </div>
            <div className="mt-2.5 rounded-xl border border-[#EEDCB9] bg-[#FBF1DF]/70 px-3 py-2.5">
              <p className="text-[10px] font-bold text-ink">Perlu Cek Dulu</p>
              <p className="text-[9px] text-cashmere mt-0.5 leading-relaxed">2 hal penting belum kamu tandai sebagai sudah dicek.</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* floating verified badge */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut', delay: 0.8 }}
        className="absolute bottom-6 left-2 rounded-xl bg-inkdeep border border-ivory/15 px-4 py-3 shadow-2xl"
      >
        <div className="flex items-center gap-2">
          <Check size={13} className="text-[#8fce9b]" strokeWidth={3} />
          <p className="text-[10px] text-ivory/85 font-medium">Identitas PIC sudah Anda cek</p>
        </div>
        <p className="mt-1 font-mono tnum text-[9px] text-ivory/45">Dicek oleh Anda · 12 Agustus</p>
      </motion.div>
    </div>
  );
}

function HeroMock() {
  return (
    <div className="relative">
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
        className="relative rounded-2xl bg-ivory text-pewter shadow-[0_40px_80px_-30px_rgba(0,0,0,0.55)] border border-gold/30 overflow-hidden"
      >
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="p-5 relative">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra">Yang Perlu Dibereskan Sekarang</p>
            <span className="relative flex h-2 w-2">
              <motion.span animate={{ scale: [1, 1.9, 1], opacity: [0.9, 0, 0.9] }} transition={{ repeat: Infinity, duration: 2.4 }} className="absolute inline-flex h-full w-full rounded-full bg-terra" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-terra" />
            </span>
          </div>
          <p className="font-serif text-lg font-semibold text-ink leading-snug">Konfirmasi kontrak WO sebelum DP berikutnya</p>
          <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">DP Rp15.000.000 jatuh tempo 28 Agustus. Dua hal penting masih perlu dicek.</p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-terra">
            Cek Sekarang <ArrowRight size={13} />
          </div>
        </div>
        <div className="border-t border-ink/8 px-5 py-3.5 flex items-center justify-between bg-white/70">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Pembayaran Terdekat</p>
            <p className="font-mono text-sm font-semibold text-ink mt-0.5">Rp15.000.000</p>
          </div>
          <StatusChip done={false} todoLabel="2 hal perlu dicek" />
        </div>
      </motion.div>
      <motion.div
        animate={{ y: [0, 7, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut', delay: 0.6 }}
        className="absolute -bottom-10 -left-2 sm:-left-6 rounded-xl bg-inkdeep border border-ivory/15 px-4 py-3 shadow-2xl"
      >
        <div className="flex items-center gap-2">
          <Check size={14} className="text-[#7fc383]" />
          <p className="text-[11px] text-ivory/85 font-medium">Identitas PIC sudah Anda cek</p>
        </div>
        <p className="mt-1 font-mono text-[10px] text-ivory/45">Dicek oleh Anda · 12 Agustus</p>
      </motion.div>
    </div>
  );
}

export default function Landing() {
  const { hasProfile, startDemo } = useStore();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yMotif = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const goDemo = () => { startDemo(); navigate('/app'); };
  const goStart = () => navigate(hasProfile ? '/app' : '/setup');

  return (
    <div className="bg-ivory text-pewter overflow-x-clip">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden" style={{ background: 'radial-gradient(ellipse 75% 60% at 62% 32%, #F7EFE9 0%, #FAF6F2 55%, #F6F1EA 100%)' }}>
        <motion.div style={{ y: yMotif }} className="kawung absolute -inset-24 opacity-[0.03] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none indigo-haze" />

        <nav className="absolute top-0 inset-x-0 z-20">
          <div className="mx-auto max-w-6xl px-6 py-6 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rotate-45 bg-terra" />
              <span className="font-serif text-xl font-semibold tracking-tight text-ink">SebelumSah<span className="text-terra">.</span></span>
            </div>
            <div className="flex items-center gap-3">
              <a data-testid="nav-link-manifesto" href="#manifesto" className="hidden sm:block text-xs font-medium text-cashmere hover:text-ink transition-colors">Manifesto</a>
              <a data-testid="nav-link-fitur" href="#fitur" className="hidden sm:block text-xs font-medium text-cashmere hover:text-ink transition-colors">Fitur</a>
              <button data-testid="header-cta-app" onClick={goStart} className="rounded-full bg-ink text-ivory px-4 py-2 text-xs font-semibold hover:bg-inkdeep transition-colors">
                {hasProfile ? 'Buka Aplikasi' : 'Mulai'}
              </button>
            </div>
          </div>
        </nav>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 mx-auto max-w-6xl px-6 pt-32 pb-24 grid lg:grid-cols-[2fr_3fr] gap-10 lg:gap-6 items-center">
          <div>
            <MaskedLine delay={0.15}>
              <span className={EYEBROW}>Untuk pasangan Indonesia</span>
            </MaskedLine>
            <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.06] text-5xl sm:text-6xl lg:text-8xl text-ink">
              <MaskedLine delay={0.3}>Cek dulu,</MaskedLine>
              <MaskedLine delay={0.45}><span className="italic text-terra">sebelum bayar.</span></MaskedLine>
            </h1>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1, delay: 1.05, ease: EASE }} className="gold-rule origin-left mt-8 w-40" />
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1, ease: EASE }} className="mt-6 max-w-xl text-base sm:text-[17px] leading-[1.65] text-[#5F5A56]">
              SebelumSah bantu kamu melihat apa yang perlu dibereskan, apa yang masih perlu dicek, dan apa yang harus dipastikan sebelum pembayaran berikutnya.
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.2, ease: EASE }} className="mt-4 font-mono text-[11px] sm:text-xs tnum uppercase tracking-[0.28em] text-ink/55">
              PLAN → CHECK → PAY → READY
            </motion.p>
            <motion.ul initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.25, ease: EASE }} className="mt-6 space-y-2.5">
              {['Rencana lebih jelas', 'Vendor lebih terkontrol', 'Pembayaran lebih tertata'].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-[15px] sm:text-base text-pewter">
                  <span className="w-[18px] h-[18px] rounded-full bg-[#EBF2EC] border border-[#CBDCCE] flex items-center justify-center"><Check size={11} strokeWidth={3} className="text-sage" /></span>
                  {t}
                </li>
              ))}
            </motion.ul>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.4, ease: EASE }} className="mt-9 flex flex-wrap items-center gap-3">
              <button data-testid="hero-cta-start" onClick={goStart} className="group rounded-full bg-ink text-ivory px-7 py-3.5 text-sm font-bold soft-shadow hover:bg-inkdeep transition-colors">
                {hasProfile ? 'Lanjut ke Aplikasi' : 'Mulai Rencanakan'}
                <ArrowRight size={15} className="inline ml-2 -mt-0.5 transition-transform group-hover:translate-x-1" />
              </button>
              <button data-testid="hero-cta-demo" onClick={goDemo} className="rounded-full border border-ink/20 bg-white/70 px-7 py-3.5 text-sm font-semibold text-ink hover:bg-white transition-colors">
                Coba Demo
              </button>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.55, ease: EASE }} className="mt-4 text-xs text-cashmere max-w-md">
              Lihat cara SebelumSah membantu dari cek vendor sampai sebelum transfer.
            </motion.p>
          </div>
          <motion.div style={{ y: yCard }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.9, ease: EASE }} className="hidden md:block">
            <HeroShowcase />
          </motion.div>
        </motion.div>

        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-cashmere/60">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Gulir</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block w-px h-8 bg-gradient-to-b from-cashmere/40 to-transparent" />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-ink/10 bg-bone py-4 overflow-hidden" aria-hidden>
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {MARQUEE.map((m) => (
                <span key={dup + m} className="flex items-center">
                  <span className="font-serif italic text-lg sm:text-xl text-ink/80 whitespace-nowrap px-6">{m}</span>
                  <span className="w-1.5 h-1.5 rotate-45 bg-gold/70 shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* PROBLEM */}
      <section className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className={`${EYEBROW} mb-5`}>Satu masalah nyata</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.12] text-ink">
              Menikah itu indah.<br />
              <span className="italic text-terra">Mentransfer belasan juta</span> ke vendor yang belum jelas itu cemas.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-5 text-base sm:text-[17px] leading-[1.65] text-[#5F5A56]">
              <p>Banyak pasangan mentransfer dana besar ke beberapa vendor saat kontrak, ketentuan pembayaran, dan referensi masih belum jelas. SebelumSah menjawab tiga pertanyaan itu dengan tenang:</p>
              <ul className="space-y-3">
                {['Apa yang sudah dicek?', 'Apa yang belum dicek?', 'Apa yang sebaiknya dilakukan berikutnya?'].map((q, i) => (
                  <li key={q} className="flex items-baseline gap-4 border-l-2 border-gold/50 pl-4">
                    <span className="font-mono text-xs text-gold">0{i + 1}</span>
                    <span className="font-serif text-lg sm:text-xl text-ink">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section id="manifesto" className="relative bg-bone py-20 sm:py-28 overflow-hidden border-y border-line">
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className={EYEBROW}>Perjalanan Sebelum Hari H</p>
            <h2 className="font-serif text-[32px] sm:text-5xl font-medium tracking-tight leading-[1.15] text-ink mt-4">
              Lima Bab Menuju Hari Bahagia
            </h2>
            <div className="gold-rule w-32 mx-auto mt-6" />
            <p className="mt-6 text-base sm:text-[17px] leading-[1.65] text-[#5F5A56]">
              Dari memilih dengan hati sampai membayar dengan lebih pasti.
            </p>
          </Reveal>

          <div className="relative mt-16 sm:mt-20">
            <div className="hidden lg:block absolute left-1/2 top-2 bottom-2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-gold/45 to-transparent" />
            <div className="space-y-16 lg:space-y-24">
              {CHAPTERS.map((c, i) => (
                <Fragment key={c.n}>
                  {i === 3 && <PauseMoment />}
                  <ChapterRow c={c} i={i} />
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section id="fitur" className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
        <Reveal>
          <SectionHead
            eyebrow="Bukan checklist pernikahan biasa"
            title="Tiga layar. Satu ketenangan."
            desc="Dirancang untuk satu momen paling menegangkan: tepat sebelum kamu mentransfer uang ke vendor."
          />
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          <Reveal delay={0.05}>
            <div data-testid="showcase-verify" className="card-lift rounded-2xl border border-ink/10 bg-white overflow-hidden h-full">
              <div className="px-5 pt-5 pb-4 border-b border-ink/8 flex items-center gap-2.5">
                <FileSearch size={16} className="text-terra" />
                <p className="text-xs font-bold text-ink uppercase tracking-wider">Vendor Verify</p>
              </div>
              <div className="p-5 space-y-3">
                {[
                  { t: 'Nama bisnis dicatat', done: true },
                  { t: 'Ketentuan pembatalan tersedia', done: false },
                  { t: 'Nama penerima dikonfirmasi ke PIC', done: false },
                ].map((r) => (
                  <div key={r.t} className="flex items-start justify-between gap-3">
                    <p className="text-xs text-pewter leading-snug">{r.t}</p>
                    <StatusChip done={r.done} />
                  </div>
                ))}
                <div className="rounded-lg bg-bone px-3 py-2.5">
                  <p className="text-[11px] text-slate-500 italic">“3 acara terbaru dicatat.”</p>
                  <p className="font-mono text-[10px] text-slate-400 mt-1">Dicek oleh Anda · 12 Agustus</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="showcase-guard" className="card-lift rounded-2xl bg-ink text-ivory overflow-hidden h-full relative">
              <div className="kawung-light absolute inset-0 opacity-[0.03] pointer-events-none" />
              <div className="relative px-5 pt-5 pb-4 border-b border-ivory/12 flex items-center gap-2.5">
                <ShieldCheck size={16} className="text-gold" />
                <p className="text-xs font-bold uppercase tracking-wider">Payment Guard</p>
              </div>
              <div className="relative p-5">
                <p className="font-serif italic text-sm text-ivory/85">Cek dulu, sebelum bayar.</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ivory/50">Sebelum Transfer</p>
                <p className="font-mono text-3xl font-semibold mt-2 tracking-tight">Rp15.000.000</p>
                <p className="text-xs text-ivory/60 mt-1">DP Tahap 2 · 28 Agustus</p>
                <div className="mt-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3">
                  <p className="text-xs font-bold text-gold">Perlu Cek Dulu</p>
                  <p className="text-[11px] text-ivory/70 mt-1 leading-relaxed">2 hal penting belum kamu tandai sebagai sudah dicek.</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div data-testid="showcase-radar" className="card-lift rounded-2xl border border-ink/10 bg-white overflow-hidden h-full">
              <div className="px-5 pt-5 pb-4 border-b border-ink/8 flex items-center gap-2.5">
                <Radar size={16} className="text-terra" />
                <p className="text-xs font-bold text-ink uppercase tracking-wider">Action Radar</p>
              </div>
              <div className="p-5 space-y-3">
                {[
                  'Konfirmasi kontrak WO sebelum DP berikutnya',
                  'Tinjau ketentuan pembatalan Catering',
                  'Jadwalkan fitting busana',
                ].map((t, i) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-gold w-5">0{i + 1}</span>
                    <p className="text-xs text-pewter leading-snug">{t}</p>
                  </div>
                ))}
                <p className="text-[11px] text-slate-400 pt-1">Selalu tahu langkah berikutnya — maksimal tiga, tidak lebih.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-bone border-y border-line py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <Sparkles size={20} className="mx-auto text-terra mb-5" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink leading-snug">
              Kami bantu kamu melihat bukti lebih jelas.<br className="hidden sm:block" />
              <span className="italic text-terra">Kamu yang menentukan langkah berikutnya.</span>
            </h2>
            <div className="mt-7 flex flex-wrap justify-center gap-2.5">
              {['Sudah Dicek', 'Perlu Dicek', 'Langkah Berikutnya'].map((t) => (
                <span key={t} className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink soft-shadow">{t}</span>
              ))}
            </div>
            <p className="mt-7 text-base leading-[1.65] text-[#5F5A56] max-w-xl mx-auto">
              SebelumSah tidak melakukan verifikasi vendor secara independen dan tidak menjamin keamanan transaksi. Tidak ada skor kepercayaan, tidak ada label aman. Hanya catatan yang rapi — <span className="font-mono text-xs tnum">Dicek oleh Anda · tanggal</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative blush-cta py-24 sm:py-32 overflow-hidden">
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] text-ink">
              “Sekarang saya <span className="italic text-terra">lebih tenang</span> sebelum transfer, karena tahu apa yang sudah jelas dan apa yang masih perlu dicek.”
            </h2>
            <div className="gold-rule w-32 mx-auto mt-8" />
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button data-testid="final-cta-start" onClick={goStart} className="rounded-full bg-ink text-ivory px-8 py-4 text-sm font-bold soft-shadow hover:bg-inkdeep transition-colors">
                {hasProfile ? 'Lanjut ke Aplikasi' : 'Mulai Rencanakan'}
              </button>
              <button data-testid="final-cta-demo" onClick={goDemo} className="rounded-full border border-ink/20 bg-white/70 px-8 py-4 text-sm font-semibold text-ink hover:bg-white transition-colors">
                Coba Demo
              </button>
            </div>
            <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.25em] text-cashmere/70">Tanpa akun · Data tersimpan di perangkatmu</p>
          </Reveal>
        </div>
      </section>

      <footer className="bg-ivory border-t border-line text-cashmere py-8">
        <div className="mx-auto max-w-6xl px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rotate-45 bg-terra" />
            <span className="font-serif text-sm font-semibold text-ink">SebelumSah<span className="text-terra">.</span></span>
          </div>
          <p className="text-xs font-mono tracking-wide">Dibuat untuk Emergent Building Indonesia · {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
